import React from "react";
import { Box } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import Actions from "../../components/Actions";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomLabel from "../../components/CustomLabel";
import {
  getAllExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
} from "../../services/ExpenseServices";
import { getUserId } from "../../services/authService";
import { getAllBranches } from "../../services/BranchesService";
import { useParams, useLocation } from "react-router-dom";

const Expenses: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const venueId = params.branchId;

  console.log("Expenses component - params:", params);
  console.log("Expenses component - venueId from params:", venueId);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("all");

  const [search, setSearch] = React.useState<string>("");
  const [branches, setBranches] = React.useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = React.useState(false);
  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const resolveBranchName = React.useCallback(
    (businessId: any, fallback?: string) => {
      const branchInfo = branches.find(
        (b: any) => String(b.business_id) === String(businessId)
      );
      if (branchInfo) return branchInfo.name || "Unknown Branch";
      if (fallback && String(fallback).trim()) return fallback;
      return "Unknown Branch";
    },
    [branches]
  );

  const getZonedParts = (date: Date, timeZone: string) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);

    return parts.reduce<Record<string, string>>((acc, part) => {
      if (part.type !== "literal") {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});
  };

  const buildDateWithCurrentTime = (dateInput?: string) => {
    const tz = "Asia/Beirut";
    const now = new Date();
    const baseDate = dateInput ? new Date(dateInput) : new Date();
    if (isNaN(baseDate.getTime())) return now.toISOString();

    const dateParts = getZonedParts(baseDate, tz);
    const timeParts = getZonedParts(now, tz);

    const beirutDateUtc = new Date(
      Date.UTC(
        Number(dateParts.year),
        Number(dateParts.month) - 1,
        Number(dateParts.day),
        Number(timeParts.hour),
        Number(timeParts.minute),
        Number(timeParts.second),
        now.getMilliseconds()
      )
    );

    return beirutDateUtc.toISOString();
  };

  const formFields: Field[] = [
    { name: "title", label: "Title", required: true },
    { name: "description", label: "Description", required: true },
    { name: "amount", label: "Amount", type: "number", required: true },
    {
      name: "branch",
      label: "Branch/Business",
      type: "dropdown",
      required: true,
      options: branches.map((branch) => ({
        label: branch.name || "Unknown Branch",
        value: branch.business_id,
      })),
    },
    { name: "date", label: "Date", type: "date" },
    { name: "note", label: "Note" },
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
  });

  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);

  React.useEffect(() => {
    const fetchExpenses = async () => {
      setLoading(true);
      try {
        const businessId = selectValue ? String(selectValue) : undefined;
        const response = await getAllExpenses(businessId, venueId ?? null);

        let expensesData: any[] = [];
        if (response && Array.isArray(response)) {
          expensesData = response;
        } else if (response && response.data) {
          expensesData = Array.isArray(response.data)
            ? response.data
            : response.data.expenses || [];
        } else if (response && response.expenses) {
          expensesData = response.expenses;
        }

        const mapped = (expensesData || []).map((e: any) => {
          const businessId =
            e.business_id ?? e.businessId ?? e.business ?? e.branch_id ?? "";
          const branchNameFromApi =
            e.business_name ?? e.branch_name ?? e.name ?? "";
          return {
            id: e.id ?? e._id ?? e.expense_id,
            title: e.title ?? e.name ?? "",
            description: e.description ?? "",
            amount: e.amount ?? e.total ?? 0,
            date: e.date ?? e.created_at ?? new Date().toISOString(),
            note: e.notes ?? "",
            createdBy: e.createdBy ?? e.user_name ?? e.user_id ?? "",
            business_id: businessId,
            branch: resolveBranchName(businessId, branchNameFromApi),
          };
        });

        const sorted = [...mapped].sort((a, b) => {
          const aTime = new Date(a.date || "").getTime();
          const bTime = new Date(b.date || "").getTime();
          return bTime - aTime;
        });

        setRows(sorted);
      } catch (error: any) {
        console.error("Error fetching expenses:", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExpenses();
  }, [refetchTrigger, selectValue, resolveBranchName]);

  React.useEffect(() => {
    if (!branches || branches.length === 0) return;
    setRows((prevRows) => {
      if (!prevRows || prevRows.length === 0) return prevRows;
      let updated = false;
      const updatedRows = prevRows.map((r: any) => {
        const resolvedName = resolveBranchName(r.business_id, r.branch);
        if (resolvedName !== r.branch) {
          updated = true;
          return { ...r, branch: resolvedName };
        }
        return r;
      });
      return updated ? updatedRows : prevRows;
    });
  }, [branches, resolveBranchName]);

  React.useEffect(() => {
    const fetchBranches = async () => {
      console.log("fetchBranches called with venueId:", venueId);
      setLoadingBranches(true);
      try {
        if (!venueId) {
          console.error("No venue ID available, venueId:", venueId);
          setBranches([]);
          return;
        }

        console.log("Calling getAllBranches with venueId:", venueId);
        const response = await getAllBranches(venueId);
        console.log("Branches API response:", response);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        console.log("Processed branches data:", branchesData);
        const normalizedBranches = Array.isArray(branchesData)
          ? branchesData
          : [];
        setBranches(normalizedBranches);

        setSelectValue((prev) => {
          const hasSelection = Array.isArray(prev)
            ? prev.length > 0
            : Boolean(prev);
          if (hasSelection) return prev;
          return "all";
        });
      } catch (error) {
        console.error("Error fetching branches:", error);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (venueId) {
      fetchBranches();
    } else {
      console.log("No venueId available, skipping branch fetch");
    }
  }, [venueId]);

  const exportCsv = React.useCallback(() => {
    if (!rows || rows.length === 0) return;
    const keys = [
      "title",
      "description",
      "amount",
      "branch",
      "date",
      "note",
      "createdBy",
    ];
    const header = keys.join(",");
    const lines = rows.map((r: any) =>
      keys
        .map((k) => {
          const v = r[k] ?? "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <CustomLabel
          text="Expenses"
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 2 }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          dateRangeConfig={{
            value: dateRange ?? undefined,
            onChange: (v: any) => setDateRange(v),
            fullWidth: false,
          }}
          selectConfig={{
             options: [
              { label: "All", value: "all" },
              ...branches.map((branch) => ({
                label: branch.name || "Unknown Branch",
                value: branch.business_id,
              })),
            ],
            value: selectValue,
            onChange: (v: any) => {
              setSelectValue(v);
            },
            placeholder: "Select branch",
            label: "Select Branch",
            size: "medium",
            searchable: false,
            clearable: true,
            multiSelect: false,
            sx: {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
            {
              label: "Export CSV",
              onClick: () => Promise.resolve(exportCsv()),
              startIcon: <FileDownloadIcon />,
              variant: "contained",
              bgColor: COLORS.MainColor,
              textColor: "#fff",
              showExportProgress: true,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<any>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loading}
          fields={[
            { key: "title", header: "Title" },
            { key: "description", header: "Description" },
            { key: "amount", header: "Amount" },
            { key: "date", header: "Date" },
              { key: "branch", header: "Branch" },
            { key: "note", header: "Note" },
            // { key: "createdBy", header: "Created By" },
          ]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "")
              .toLowerCase()
              .trim();

            const filtered = rows.filter((r: any) => {
              if (normalizedQuery) {
                const haystack = [
                  r.title,
                  r.description,
                  r.amount,
                  r.date,
                  r.branch,
                  r.note,
                  r.createdBy,
                ]
                  .filter(Boolean)
                  .map((v: any) => String(v).toLowerCase())
                  .join(" ");

                if (!haystack.includes(normalizedQuery)) return false;
              }
              if (dateRange) {
                const rowDate = new Date(r.date);
                let startDate: Date | null = null;
                let endDate: Date | null = null;
                if (Array.isArray(dateRange)) {
                  if (dateRange[0]) startDate = new Date(dateRange[0]);
                  if (dateRange[1]) endDate = new Date(dateRange[1]);
                } else {
                  if (dateRange.start) startDate = new Date(dateRange.start);
                  if (dateRange.end) endDate = new Date(dateRange.end);
                }

                if (startDate && rowDate < startDate) return false;
                if (endDate && rowDate > endDate) return false;
              }

              return true;
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: "0 0 10px 10px",
            padding: "16px",
            marginTop: "-10px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CustomLabel
            text="Total Amount:"
            sx={{ color: COLORS.typographyColor }}
          />
          <CustomLabel
            text={new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(
              rows
                .filter((r: any) => {
                  // Filter by search
                  const normalizedQuery = String(search || "")
                    .toLowerCase()
                    .trim();

                  if (normalizedQuery) {
                    const haystack = [
                      r.title,
                      r.description,
                      r.amount,
                      r.date,
                      r.branch,
                      r.note,
                      r.createdBy,
                    ]
                      .filter(Boolean)
                      .map((v: any) => String(v).toLowerCase())
                      .join(" ");

                    if (!haystack.includes(normalizedQuery)) return false;
                  }

                  // Filter by date range
                  if (dateRange) {
                    const rowDate = new Date(r.date);
                    let startDate: Date | null = null;
                    let endDate: Date | null = null;

                    if (Array.isArray(dateRange)) {
                      if (dateRange[0]) startDate = new Date(dateRange[0]);
                      if (dateRange[1]) endDate = new Date(dateRange[1]);
                    } else {
                      if (dateRange.start)
                        startDate = new Date(dateRange.start);
                      if (dateRange.end) endDate = new Date(dateRange.end);
                    }

                    if (startDate && rowDate < startDate) return false;
                    if (endDate && rowDate > endDate) return false;
                  }

                  return true;
                })
                .reduce((sum: number, r: any) => {
                  const amount = Number(r.amount) || 0;
                  return sum + amount;
                }, 0)
            )}
            fontWeight="bold"
            sx={{ color: COLORS.MainColor }}
          />
        </Box>
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Expenses` : "Add Expenses"}
          submitLabel={editingRow ? "Save" : "Create"}
          fields={
            editingRow
              ? formFields.map((f) =>
                  f.name === "date" ? { ...f, disabled: true } : f
                )
              : [...formFields]
          }
          disableSubmitUntilValid
          initialValues={
            editingRow
              ? {
                  title: editingRow.title || "",
                  description: editingRow.description || "",
                  amount: editingRow.amount || "",
                  branch: String(
                    editingRow.business_id ?? editingRow.businessId ?? selectValue ?? ""
                  ),
                  date: editingRow.date || "",
                  note: editingRow.note || "",
                }
              : {
                  branch:
                    !Array.isArray(selectValue) && selectValue !== "all"
                      ? String(selectValue ?? "")
                      : "",
                }
          }
          onSubmit={async (values) => {
            try {
              if (editingRow) {
                const userId = getUserId();
                if (!userId) {
                  toast.error("User not authenticated. Please log in again.", {
                    position: "top-right",
                  });
                  return;
                }
                const payload = {
                  title: values.title || "",
                  description: values.description || "",
                  amount: values.amount || "",
                  notes: values.note || "",
                  user_id: userId,
                  business_id: String(
                    values.branch ?? editingRow.business_id ?? ""
                  ),
                };

                await updateExpense(String(editingRow.id), payload);
                toast.success("Expense updated successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              } else {
                const userId = getUserId();
                if (!userId) {
                  toast.error("User not authenticated. Please log in again.", {
                    position: "top-right",
                  });
                  return;
                }
                const payload = {
                  title: values.title || "",
                  description: values.description || "",
                  amount: values.amount || "",
                  notes: values.note || "",
                  created_at: buildDateWithCurrentTime(values.date),
                  user_id: userId,
                  business_id: String(values.branch ?? ""),
                };

                await createExpense(payload);
                toast.success("Expense created successfully!", {
                  position: "top-right",
                });
                setRefetchTrigger((prev) => prev + 1);
              }
              setEditOpen(false);
              setEditingRow(null);
            } catch (error: any) {
              console.error("Error saving expense:", error);
              toast.error("Failed to save expense. Please try again.", {
                position: "top-right",
              });
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteExpense(String(toDeleteId));
              toast.success("Expense deleted successfully!", {
                position: "top-right",
              });
              setRefetchTrigger((prev) => prev + 1);
            } catch (error: any) {
              console.error("Error deleting expense:", error);
              toast.error("Failed to delete expense. Please try again.", {
                position: "top-right",
              });
            } finally {
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default Expenses;
